import React, {useState} from 'react';
import {Field, FieldArray, Formik} from 'formik/dist';
import {Button, Form as AntdForm} from 'antd/lib';
import * as Yup from 'yup';

import {Gift, GiftPhoto} from 'types/index';
import * as modes from 'constants/modes';
import * as deliveryCarriers from 'constants/deliveryCarriers';

import FormTextInput from 'components/form/TextInput';
import DatePicker from 'components/form/DatePicker';
import FormTextArea from 'components/form/TextArea';
import DropDownSelect from 'components/form/DropDown';
import RadioInput from 'components/form/RadioInput';
import * as statuses from 'constants/statuses';
import * as socialStatuses from 'constants/socialStatuses';

import ListAttachments from './components/ListAttachments';
import ConfirmGiftSwitcher from './components/ApproveGiftSwitcher';
import ConfirmTermsOfConditionsCheckbox from './components/ConfirmTermsOfConditionsCheckbox';
import styles from './form.module.scss';

export interface InitialValues extends Gift {
    addedPhotos?: File[];
    removedPhotos?: GiftPhoto[];
}

interface FormProps {
    isAdmin: boolean;
    mode: 'create' | 'update';
    initialValues: InitialValues;
    onClose(): void;
    onSubmit(fields: any): void;
}

const parentTypes = [
    {label: 'Батько / Мати', value: 'parent'},
    {label: 'Опікун', value: 'guardian'},
];

const deliveryCarriersList = [{label: 'Новою Поштою', value: deliveryCarriers.NOVA_POSHTA}];

const statusesList = [
    {value: statuses.GIFT_STATUS_FREE, label: 'Вільний'},
    {value: statuses.GIFT_STATUS_BOOKED, label: 'Заброньовано'},
    {value: statuses.GIFT_STATUS_CONFIRMED, label: 'Підтверджено'},
    {value: statuses.GIFT_STATUS_DONE, label: 'Виконаний'},
];

const socialStatusList = [
    {value: socialStatuses.DISABLED, label: 'дитина з інвалідністю'},
    {value: socialStatuses.ORPHAN, label: 'сироти'},
    {value: socialStatuses.FATHER_DIED_ATO, label: 'батьки, які загинули в АТО (безвісти зникли)'},
    {value: socialStatuses.CANCER, label: 'онкохворі діти'},
    {value: socialStatuses.DIFFICULT_CIRCUMSTANCES, label: 'складні життєві обставини'},
];

const FIELDS = {
    itemNumber: 'itemNumber',
    childName: 'child.name',
    childBirthDay: 'child.birthDay',
    childSocialStatus: 'child.socialStatus',
    childDiagnosis: 'child.diagnosis',
    childCity: 'child.city',
    parentType: 'parent.type',
    parentName: 'parent.name',
    parentPhone: 'parent.phone',
    parentSocialNetworkLink: 'parent.socialNetworkLink',
    description: 'description',
    attachmentAvatars: 'attachments.avatars',
    attachmentWishes: 'attachments.wishes',
    attachmentDocuments: 'attachments.documents',
    attachmentReports: 'attachments.reports',
    videoReportLink: 'videoReportLink',
    status: 'status',
    bookedPersonName: 'bookedPerson.name',
    bookedPersonPhone: 'bookedPerson.phone',
    bookedPersonComment: 'bookedPerson.comment',
    isApproved: 'isApproved',
    deliveryToOfficeAddress: 'deliveryTo.officeAddress',
    deliveryToOfficeNumber: 'deliveryTo.officeNumber',
    deliveryToCarrier: 'deliveryTo.carrier',
};
const phoneRegExp = /^\+\d{2}\s\(\d{3}\)\s\d{3}\s\d{2}\s\d{2}$/;
const phoneMask = [
    '+',
    '3',
    '8',
    ' ',
    '(',
    /[0-9]/,
    /\d/,
    /\d/,
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
];

const requiredFieldMessage = "Обов'язкове поле";
const requiredNumberMessage = 'Введіть валідний номер';

const validationSchema = Yup.object().shape({
    child: Yup.object().shape({
        name: Yup.string().required(requiredFieldMessage),
        socialStatus: Yup.string().required(requiredFieldMessage),
        city: Yup.string().required(requiredFieldMessage),
    }),
    parent: Yup.object().shape({
        name: Yup.string().required(requiredFieldMessage),
        phone: Yup.string()
            .required(requiredFieldMessage)
            .matches(phoneRegExp, requiredNumberMessage),
        socialNetworkLink: Yup.string().required(requiredFieldMessage),
    }),
    description: Yup.string().required(requiredFieldMessage),
    bookedPerson: Yup.object().shape({
        phone: Yup.string().matches(phoneRegExp, requiredNumberMessage),
    }),
    deliveryTo: Yup.object().shape({
        officeAddress: Yup.string().required(requiredFieldMessage),
        officeNumber: Yup.string().required(requiredFieldMessage),
        carrier: Yup.string().required(requiredFieldMessage),
    }),
});

const FormContent = ({formProps, isAdmin, mode, onClose}: any): JSX.Element => {
    const [isConfirmedTermsOfConditions, setConfirmTermsOfConditions] = useState(false);
    const selectedStatus = formProps.values[FIELDS.status];
    const selectedChild = formProps.values.child;
    const selectedChildSocialStatus = selectedChild?.socialStatus;
    const isApproved = formProps.values[FIELDS.isApproved];

    return (
        <AntdForm onSubmit={formProps.handleSubmit}>
            <div className={styles.scroll}>
                {!isAdmin && (
                    <div className={styles.amount}>
                        Сума подарунка до <strong>500 грн</strong>. (Вище за погодженням з організатором) або необхідна
                        річ для дитини.
                        <br />
                        Телефони, планшети, ноутбуки до списку подарунків не входять.
                        <br />
                        Ми не оплачуємо операції та реабілітації.
                    </div>
                )}
                {mode === modes.MODE_CREATE && <div>Заповніть дані, щоб ми могли зв'язатися з вами</div>}
                {mode === modes.MODE_UPDATE && !isApproved && (
                    <div className="not-approved-badge">заявка не підтверджена</div>
                )}

                {isAdmin && (
                    <AntdForm.Item label="Номер заявки">
                        <Field name={FIELDS.itemNumber} component={FormTextInput} placeholder="" />
                    </AntdForm.Item>
                )}

                <AntdForm.Item label="Прізвище Ім'я дитини">
                    <Field maxLength={50} name={FIELDS.childName} component={FormTextInput} placeholder="" />
                </AntdForm.Item>
                <AntdForm.Item label="Дата народження дитини">
                    <Field
                        maxLength={50}
                        name={FIELDS.childBirthDay}
                        component={DatePicker}
                        placeholder="Виберіть дату"
                    />
                </AntdForm.Item>
                <AntdForm.Item label="Соціальний статус">
                    <Field
                        name={FIELDS.childSocialStatus}
                        component={DropDownSelect}
                        setFieldValue={formProps.setFieldValue}
                        options={socialStatusList}
                        placeholder=""
                    />
                </AntdForm.Item>
                <AntdForm.Item>
                    <Field name={FIELDS.parentType} component={RadioInput} options={parentTypes} placeholder="" />
                </AntdForm.Item>
                {selectedChildSocialStatus === socialStatuses.DISABLED && (
                    <AntdForm.Item label="Повний діагноз дитини">
                        <Field maxLength={50} name={FIELDS.childDiagnosis} component={FormTextInput} placeholder="" />
                    </AntdForm.Item>
                )}
                <AntdForm.Item label="Прізвище Ім'я батька або матері / опікуна">
                    <Field maxLength={50} name={FIELDS.parentName} component={FormTextInput} placeholder="" />
                </AntdForm.Item>

                <AntdForm.Item label="Телефон батька або матері / опікуна">
                    <Field
                        maxLength={50}
                        name={FIELDS.parentPhone}
                        mask={phoneMask}
                        component={FormTextInput}
                        placeholder="+38 (___) ___ __ __"
                    />
                </AntdForm.Item>
                <AntdForm.Item label="Посилання на соціальну мережу (Facebook, Instagram)">
                    <Field
                        maxLength={50}
                        name={FIELDS.parentSocialNetworkLink}
                        component={FormTextInput}
                        placeholder=""
                    />
                </AntdForm.Item>
                <AntdForm.Item label="Місто проживання">
                    <Field maxLength={50} name={FIELDS.childCity} component={FormTextInput} placeholder="" />
                </AntdForm.Item>

                <AntdForm.Item label="Надсилання подарунка">
                    <Field
                        name={FIELDS.deliveryToCarrier}
                        component={RadioInput}
                        options={deliveryCarriersList}
                        placeholder=""
                    />
                </AntdForm.Item>
                <AntdForm.Item label="Адреса пошти">
                    <Field
                        maxLength={50}
                        name={FIELDS.deliveryToOfficeAddress}
                        component={FormTextInput}
                        placeholder=""
                    />
                    <small>введіть адресу у форматі: місто, вулиця, будинок</small>
                </AntdForm.Item>
                <AntdForm.Item label="№ Відділення">
                    <Field
                        maxLength={50}
                        name={FIELDS.deliveryToOfficeNumber}
                        component={FormTextInput}
                        placeholder=""
                    />
                </AntdForm.Item>
                <AntdForm.Item label="Побажання">
                    <Field name={FIELDS.description} component={FormTextArea} placeholder="" maxLength={120} />
                </AntdForm.Item>
                <AntdForm.Item>
                    <FieldArray
                        name={FIELDS.attachmentAvatars}
                        render={(helpers: any) => {
                            return (
                                <ListAttachments
                                    {...helpers}
                                    mode={mode}
                                    attachmentName="avatars"
                                    uploadTitle="Завантажити фото дитини з листом"
                                />
                            );
                        }}
                    />
                </AntdForm.Item>
                <AntdForm.Item>
                    <FieldArray
                        name={FIELDS.attachmentWishes}
                        render={(helpers: any) => {
                            return (
                                <ListAttachments
                                    {...helpers}
                                    mode={mode}
                                    attachmentName="wishes"
                                    uploadTitle="Завантажити крупним планом лист дитини"
                                />
                            );
                        }}
                    />
                </AntdForm.Item>
                <AntdForm.Item>
                    <FieldArray
                        name={FIELDS.attachmentDocuments}
                        render={(helpers: any) => (
                            <ListAttachments
                                {...helpers}
                                mode={mode}
                                attachmentName="documents"
                                uploadTitle="Завантажити документи, що підтверджують соціальний статус"
                            />
                        )}
                    />
                </AntdForm.Item>
                {mode === modes.MODE_UPDATE && (
                    <>
                        {selectedStatus !== statuses.GIFT_STATUS_FREE && (
                            <div>
                                <AntdForm.Item label="Прізвище Ім'я Виконавця">
                                    <Field name={FIELDS.bookedPersonName} component={FormTextInput} placeholder="" />
                                </AntdForm.Item>
                                <AntdForm.Item label="Телефон Виконавця">
                                    <Field
                                        maxLength={50}
                                        name={FIELDS.bookedPersonPhone}
                                        mask={phoneMask}
                                        component={FormTextInput}
                                        placeholder="+38 (___) ___ __ __"
                                    />
                                </AntdForm.Item>
                                <AntdForm.Item label="Коментар, посилання на профіль у соцмережах">
                                    <Field name={FIELDS.bookedPersonComment} component={FormTextArea} />
                                </AntdForm.Item>
                            </div>
                        )}
                        <AntdForm.Item>
                            <Field
                                name={FIELDS.status}
                                component={DropDownSelect}
                                setFieldValue={formProps.setFieldValue}
                                options={statusesList}
                                placeholder=""
                            />
                        </AntdForm.Item>

                        <AntdForm.Item>
                            <Field name={FIELDS.isApproved} component={ConfirmGiftSwitcher} placeholder="" />
                        </AntdForm.Item>

                        {selectedStatus === statuses.GIFT_STATUS_DONE && (
                            <>
                                <AntdForm.Item label="Звіт">
                                    <FieldArray
                                        name={FIELDS.attachmentReports}
                                        render={(helpers: any) => {
                                            return (
                                                <ListAttachments
                                                    {...helpers}
                                                    mode={mode}
                                                    attachmentName="reports"
                                                    uploadTitle="Завантажити фото для звіту"
                                                />
                                            );
                                        }}
                                    />
                                </AntdForm.Item>
                                <AntdForm.Item label="Посилання на youtube відео">
                                    <Field
                                        maxLength={50}
                                        name={FIELDS.videoReportLink}
                                        component={FormTextInput}
                                        placeholder=""
                                    />
                                </AntdForm.Item>
                            </>
                        )}
                    </>
                )}

                {!isAdmin && (
                    <div className="сonfirm__terms_conditions">
                        <ConfirmTermsOfConditionsCheckbox
                            onChange={setConfirmTermsOfConditions}
                            checked={isConfirmedTermsOfConditions}
                        />
                    </div>
                )}

                <div className={styles.footer}>
                    <Button color="secondary" onClick={onClose} className="custom-btn custom-btn__cancel">
                        Відмінити
                    </Button>
                    {isAdmin ? (
                        <Button disabled={!formProps.dirty} htmlType="submit" className="custom-btn custom-btn__action">
                            Зберегти
                        </Button>
                    ) : (
                        <Button
                            disabled={!formProps.dirty || !isConfirmedTermsOfConditions}
                            type="primary"
                            htmlType="submit"
                            className="custom-btn custom-btn__action"
                        >
                            Надіслати
                        </Button>
                    )}
                </div>
            </div>
        </AntdForm>
    );
};

const Form = ({isAdmin, mode, onSubmit, initialValues, onClose}: FormProps): JSX.Element => (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {(props: any) => (
            <FormContent
                isAdmin={isAdmin}
                mode={mode}
                initialValues={initialValues}
                formProps={props}
                onClose={onClose}
            />
        )}
    </Formik>
);

export default Form;
